/**
 * Detecting on a crop around the locked player, so a small body far from the
 * camera is seen large by the model.
 *
 * The pose model works on a fixed internal size. Hand it the whole 1080p frame
 * and a player thirty feet back becomes forty pixels tall inside it, which is
 * where the landmarks start to wander. Hand it a box around that player instead
 * and they fill the model's input, at the pixels the camera actually captured.
 * Same camera, same model, several times more detail on the person who matters.
 *
 * Only used when the player is small. Up close the whole frame is the box.
 * Every so often, and whenever the crop comes back empty, the whole frame is
 * searched again so a player who moved fast, or someone new, is not missed.
 *
 * This file only decides the box. The crop is cut and the landmarks are put
 * back into whole frame units by public/js/trackengine.js, wherever the models
 * run (the tracking worker, or the page when a worker cannot).
 */

const MARGIN = 0.55;          // box grows by this much of its size on each side
const SMALL = 0.09;           // shoulders narrower than this fraction of the frame: crop
const FULL_EVERY = 12;        // frames between full-frame searches while cropping

/**
 * Finding a player nobody is locked on yet. The whole frame, shrunk to the
 * model's input, loses a player far back: at a sixth of the frame's height they
 * are a few dozen pixels tall in it and the detector never fires. So between
 * whole-frame looks the search walks overlapping tiles of the camera's own
 * pixels, 2x2, then 3x3, then 4x4, one per frame: every part of the frame is
 * seen up close within about a second. A hit becomes the box, and the box
 * follows the player from then on. Measured with scripts/far-eval.mjs.
 */
const grid = (n, size) => {
  const out = [], step = n > 1 ? (1 - size) / (n - 1) : 0;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) out.push({ x: c * step, y: r * step, w: size, h: size });
  return out;
};
const TILES = [null, ...grid(2, 0.6), null, ...grid(3, 0.42), null, ...grid(4, 0.32)];

const RELOOK = 4;             // frames spent looking again, wider, where the player was lost

let box = null, frame = 0, scan = 0, relook = null;

/**
 * Forget the box: the player was lost, or a new session. A player lost from
 * the zoomed box is nearly always still near it (a quick move, a blur), so the
 * next few frames look again at a box twice the size around the same spot
 * before the far search starts over. Measured with scripts/far-eval.mjs.
 */
export function reset() {
  relook = box ? { n: RELOOK, box: grow(box, 2) } : null;
  box = null; frame = 0;
}
const grow = (b, k) => {
  const w = Math.min(1, b.w * k), h = Math.min(1, b.h * k);
  return { x: Math.min(Math.max(0, b.x + b.w / 2 - w / 2), 1 - w), y: Math.min(Math.max(0, b.y + b.h / 2 - h / 2), 1 - h), w, h };
};

/** Whether the next plan() would look at a tile of the search, for tests. */
export const TILE_COUNT = TILES.filter(Boolean).length;

/** The box the model is currently looking through, in 0..1 frame units, or null. */
export function current() { return box; }

/**
 * Remember where the locked player is, from their landmarks, for the next frame.
 * Widened by MARGIN so a step in any direction stays inside it.
 */
export function follow(lm) {
  if (!Array.isArray(lm) || !lm.length) return;
  let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
  for (const p of lm) {
    if (!p || (p.visibility ?? 1) < 0.3) continue;
    if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x;
    if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y;
  }
  if (x1 <= x0 || y1 <= y0) return;
  const w = x1 - x0, h = y1 - y0;
  const mx = w * MARGIN, my = h * MARGIN;
  box = {
    x: Math.max(0, x0 - mx), y: Math.max(0, y0 - my),
    w: Math.min(1, x1 + mx) - Math.max(0, x0 - mx),
    h: Math.min(1, y1 + my) - Math.max(0, y0 - my),
  };
}

/** Shoulder width of a body in frame units, the size cue everything keys off. */
export function shoulderWidth(lm) {
  const a = lm?.[11], b = lm?.[12];
  return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
}

/**
 * The box to show the pose model on the next frame, in 0..1 frame units, or
 * null for the whole frame. Called once per frame the pose model reads: every
 * FULL_EVERY-th of them is a whole frame search even while cropping.
 */
export function plan(playerLm) {
  frame++;
  if (playerLm) {
    const small = shoulderWidth(playerLm) < SMALL;
    return small && box && frame % FULL_EVERY !== 0 ? box : null;
  }
  // Nobody locked yet: a body a tile just found is followed until it is
  // picked as the player (or lost, and reset() sends the search on).
  if (box) return box;
  if (relook?.n > 0) { relook.n--; return relook.box; }
  scan = (scan + 1) % TILES.length;
  return TILES[scan];
}
